import { useSelector } from 'react-redux';
import useDebounce from './debounce';
import { useEffect, useMemo, useState } from 'react';
import { useProject } from '../../project/project-hooks/useProject';
import { hasErrorsOnAmount } from '../../payment/payment-model';
import { canChoosePaymentMethod } from '../../payment/payment-form-config';
import { useCallback } from 'react';

export const useFundAsGiftPaymentInputValidation = () => {
    const [amountError, setAmountError] = useState(null);
    const project = useProject();
    const paymentForm = useSelector((state) => state.paymentForm);
    const paymentConfig = useSelector((state) => state.paymentConfig);
    const debouncedAmount = useDebounce(paymentForm?.amount, 1000);

    let giveMinAmount =
        project?.profile?.giveMinAmount || paymentConfig?.figGiveMinAmount || paymentConfig?.giveMinAmount;
    let giveMaxAmount = paymentConfig?.figGiveMaxAmount || paymentConfig?.giveMaxAmount;

    const canChoosePaymentMethodFromState = useMemo(
        () =>
            canChoosePaymentMethod({
                amount: paymentForm.amount,
                giveMinAmount,
                giveMaxAmount,
                rewards: paymentForm.rewards,
            }),
        [paymentForm.amount, paymentForm.rewards, giveMinAmount, giveMaxAmount]
    );

    const reValidate = useCallback(
        (amount, updateState = false) => {
            const error = hasErrorsOnAmount({
                amount,
                giveMinAmount,
                giveMaxAmount,
            });
            if (updateState) {
                setAmountError({ amount: error });
            }
            return error;
        },
        [giveMinAmount, giveMaxAmount]
    );

    useEffect(() => {
        if (paymentForm?.amount) {
            const error = reValidate(paymentForm?.amount);
            if (!error) {
                setAmountError(null);
                return;
            }
        }

        if (debouncedAmount) {
            const error = reValidate(debouncedAmount);
            if (error) {
                setAmountError({ amount: error });
                return;
            }
        }

        setAmountError(null);
    }, [paymentForm?.amount, debouncedAmount, reValidate]);

    return { amountError, reValidate, canChoosePaymentMethodFromState };
};
